import {
  CSSettingsType,
  OrSettingsEnum,
  OrganTransportationTypeSettingsType,
  OrSettingsType,
  RouteTypeSettingsType,
  RSSettingsType,
  StartTimeSettingsType,
  StartLocationSettingsType,
} from '@lp/features/RoutePlanner/types';
import { RouteTypeSettingsEnum, StartLocationObjectType } from '@/api/apis/mission-planner';
import { getLocationAddress, zonedDateToString } from '@/utils/helpers';
import { queryClient } from '@/queries';
import { hubQueries } from '@/queries/hub';
import { NameByValue } from '@/types';
import { DH_TXC_TRANSPORTATION_TYPE_NAME, SUGGESTED_ROUTE_TYPE_NAME } from '../config';

export const UNRESOLVED_HUB_LABEL = '—';

export const getOrSettingsText = (data: OrSettingsType['value']): string => {
  switch (data.type) {
    case OrSettingsEnum.OrRange:
      return `Within Range (Current OR +${data.offset}h)`;
    case OrSettingsEnum.CustomOr:
      return `Custom (${zonedDateToString(data.selectedOr)}${data.offset ? ` +${data.offset}h` : ''})`;
    case OrSettingsEnum.CurrentOr:
    default:
      return 'Current';
  }
};

export const getRouteTypeSettingsText = (data: RouteTypeSettingsType['value']): string | null => {
  if (data === RouteTypeSettingsEnum.Full) return null;

  return SUGGESTED_ROUTE_TYPE_NAME[data];
};

export const getCSSettingsText = (data: CSSettingsType['value']): string => {
  if (!data) return 'Any';
  if (!data.hubCode) return data.employeeFullName;

  return `${data.employeeFullName} (${data.hubCode})`;
};

export const getRSSettingsText = (data: RSSettingsType['value']): string => {
  if (!data) return 'Any';
  if (!data.location) return data.employeeFullName;

  return `${data.employeeFullName} (${data.location.city}, ${data.location?.state})`;
};

export const getHubSettingsText = (hubId: string | null): string => {
  if (!hubId) return 'Any';

  const hub = queryClient.getQueryData(hubQueries.item(hubId).queryKey);

  return hub?.code || UNRESOLVED_HUB_LABEL;
};

export const getStartLocationSettingsText = (
  data: StartLocationSettingsType['value'],
  resolveSubdivisionName: (stateCode?: string | null, countryCode?: string | null) => string | undefined,
): string => {
  if (!data) return 'Select';

  const state =
    resolveSubdivisionName?.(data.location?.state, data.location?.country) ??
    data.location?.state ??
    data.location?.country ??
    '';

  switch (data.type) {
    case StartLocationObjectType.Hub:
      return `Hub: NOP ${data.code}`;
    case StartLocationObjectType.DH:
      return `DH: ${data.code}, ${data.location.city}, ${state}`;
    case StartLocationObjectType.TXC:
      return `TXC: ${data.code}, ${data.location.city}, ${state}`;
    case StartLocationObjectType.Airport:
      return `FBO: ${data.iata || data.icao}`;
    case StartLocationObjectType.Custom:
    default:
      return data.customTitle || getLocationAddress(data.location, resolveSubdivisionName);
  }
};

export const getStartTimeSettingsText = (data: StartTimeSettingsType['value']): string => {
  return zonedDateToString(data);
};

export const getTransportationTypeSettingsText = (data: OrganTransportationTypeSettingsType['value']): string => {
  return DH_TXC_TRANSPORTATION_TYPE_NAME[data];
};

export const getOrRangeText = (offset: number): string => `From OR to OR +${offset}h`;

export const getOrRangeListItems = (length: number): NameByValue[] => {
  return Array(length)
    .fill('_')
    .map((_, index) => ({
      name: getOrRangeText(index + 1),
      value: `${index + 1}`,
    }));
};
